import { type ReactNode, useEffect, useRef, useState } from 'react'
import { Animated, Pressable, ScrollView, type ViewStyle } from 'react-native'
// `Animated` (above) is React Native's legacy API, still used by the loading
// pulse. The "Deposit to" dropdown motion uses `react-native-reanimated`,
// imported here as `Reanimated` so the two namespaces don't collide.
import Reanimated, {
  Easing,
  Keyframe,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated'

import { FigmaCheckIcon, FigmaChevronDownIcon } from '../icons/FigmaSystemIcons'
import { StatusIcon } from '../icons/StatusIcon'
import { usePulse } from '../hooks/usePulse'
import { useFunkitTranslation } from '../providers/FunkitI18nProvider'
import { Box, Text, useTheme } from '../theme/restyle'

// Motion tokens for the "Deposit to" dropdown open/close — transitions.dev
// `menu-dropdown` recipe translated to reanimated. The web recipe grows the
// menu from its trigger (transform-origin top, scale 0.97→1, opacity 0→1); RN
// has no transform-origin, so a small translateY (-4→0) gives the same
// "emerging from the trigger top" read. Open = Fast (250ms), close = Quick
// (150ms), Smooth ease-out easing (cubic-bezier(0.22, 1, 0.36, 1)).
const DROPDOWN_OPEN_DURATION = 250
const DROPDOWN_CLOSE_DURATION = 150
const DROPDOWN_EASING = Easing.bezier(0.22, 1, 0.36, 1)
const DROPDOWN_PRE_SCALE = 0.97
const DROPDOWN_TRANSLATE_Y = -4

const AnimatedPressable = Reanimated.createAnimatedComponent(Pressable)

// The menu is conditionally rendered (closed = unmounted), so the open/close
// motion rides reanimated layout animations — `entering` on mount, `exiting`
// on unmount — the same pattern as AddressCopiedToast. Enter grows from the
// trigger top (opacity + scale 0.97→1 + translateY -4→0); exit reverses it.
// Easing lives on the destination keyframe (per reanimated's Keyframe API).
const menuEnter = new Keyframe({
  0: {
    opacity: 0,
    transform: [
      { translateY: DROPDOWN_TRANSLATE_Y },
      { scale: DROPDOWN_PRE_SCALE },
    ],
  },
  100: {
    opacity: 1,
    transform: [{ translateY: 0 }, { scale: 1 }],
    easing: DROPDOWN_EASING,
  },
}).duration(DROPDOWN_OPEN_DURATION)
const menuExit = new Keyframe({
  0: {
    opacity: 1,
    transform: [{ translateY: 0 }, { scale: 1 }],
  },
  100: {
    opacity: 0,
    transform: [
      { translateY: DROPDOWN_TRANSLATE_Y },
      { scale: DROPDOWN_PRE_SCALE },
    ],
    easing: DROPDOWN_EASING,
  },
}).duration(DROPDOWN_CLOSE_DURATION)
// The dimming scrim fades in/out (opacity only) in sync with the menu.
const scrimEnter = new Keyframe({
  0: { opacity: 0 },
  100: { opacity: 1, easing: DROPDOWN_EASING },
}).duration(DROPDOWN_OPEN_DURATION)
const scrimExit = new Keyframe({
  0: { opacity: 1 },
  100: { opacity: 0, easing: DROPDOWN_EASING },
}).duration(DROPDOWN_CLOSE_DURATION)

/**
 * One selectable funding source in {@link SelectMethodScreen}. Brand icons are
 * host-supplied (`icon` / `trailing`) so the package stays asset- and
 * `react-native-svg`-free; the screen renders a themed circular slot for each.
 */
export interface PaymentMethodOption {
  /** Stable id passed back to `onSelectMethod`. */
  id: string
  title: string
  /** Secondary line: balance, limit, timing, etc. */
  subtitle?: string
  /** Leading icon, centered in the themed circle. */
  icon?: ReactNode
  /** Trailing accessory, e.g. a token cluster for the Crypto row. */
  trailing?: ReactNode
  /**
   * Shown but not selectable — e.g. a cash payout whose minimum the balance
   * can't meet. Dimmed rather than hidden so the option stays discoverable.
   */
  disabled?: boolean
}

/**
 * One option in the "Deposit to" receive-token dropdown (Figma: Fun Mobile /
 * Add money). Mirrors web's `ReceiveTokenDropdown` items. The icon is supplied
 * by the flow (keeping this screen asset-free) and `label` is the composed
 * destination text, e.g. `"USDG (Perps)"`.
 */
export interface ReceiveTokenOption {
  id: string
  label: string
  icon?: ReactNode
}

export interface SelectMethodScreenProps {
  methods: PaymentMethodOption[]
  onSelectMethod: (id: string) => void
  /** Sheet title — the caller owns the copy ("Add money" vs "Withdraw"). */
  title: string
  /**
   * Receive-token candidates shown in the "Deposit to" dropdown above the
   * payment methods (web: `ReceiveTokenDropdown`). When set, the screen shows
   * the dropdown + a "Pay with" section label. Omit to render just the
   * methods list (back-compat with hosts that don't pass candidates).
   */
  receiveTokens?: ReceiveTokenOption[]
  selectedReceiveTokenId?: string
  onSelectReceiveToken?: (id: string) => void
  /**
   * Show the loading skeleton instead of the method list (Figma: Fun Mobile /
   * Add money, loading). Set while the funding sources are still being fetched
   * (e.g. the Swapped cash methods) so the list doesn't pop in row-by-row.
   */
  loading?: boolean
  /**
   * Number of skeleton rows rendered while `loading`. Defaults to 5 when the
   * deposit selector shows, 6 otherwise (it reclaims the selector's room).
   */
  skeletonCount?: number
  /**
   * Show the error state instead of the list (Figma: Fun Mobile / Add money,
   * error) — a "Something went wrong" message + a "Try again" button. Takes
   * precedence over `loading`. Set when fetching the funding sources failed.
   */
  error?: boolean
  /** "Try again" handler shown with the error state. */
  onRetry?: () => void
  /** Error-state copy — the caller owns it (generic fetch failure vs
      withdraw's terminal states). */
  errorTitle: string
  errorMessage: string
  /**
   * Open the "Deposit to" dropdown on mount. For previews/tests of the expanded
   * state (Figma node 11200:8055); hosts leave this `false` and let the user
   * tap the trigger.
   */
  initialDropdownOpen?: boolean
  /**
   * Secondary line under the list, for why part of it is missing (web's
   * `NoCashMethodsNote`). A note, not a takeover: the rows that DID load stay
   * usable.
   */
  note?: string
}

/**
 * The "Add money" funding-source picker (Figma: Fun Mobile / Add money). A
 * bottom-sheet-style surface — grabber + centered title + a scrollable list of
 * payment methods — built from the connect-rn Restyle theme so it tracks the
 * active light/dark scheme. Selecting a method (e.g. Crypto) routes the host to
 * the next screen (Transfer/UDA).
 *
 * When `receiveTokens` are given, a fixed "Deposit to" selector sits above the
 * scrollable list. Opening it floats the option list as an overlay (it does
 * NOT reflow the list) over a dimming scrim — matching web's
 * `ReceiveTokenDropdown`.
 */
export function SelectMethodScreen(props: SelectMethodScreenProps) {
  const { t } = useFunkitTranslation()
  const {
    methods,
    onSelectMethod,
    title,
    loading = false,
    skeletonCount,
    error = false,
    onRetry,
    note,
    errorTitle,
    errorMessage,
    receiveTokens,
    selectedReceiveTokenId,
    onSelectReceiveToken,
    initialDropdownOpen = false,
  } = props
  const theme = useTheme()
  const selected = receiveTokens?.find((t) => t.id === selectedReceiveTokenId)
  const showDropdown = receiveTokens && receiveTokens.length > 0 && selected

  // Dropdown open-state lives here (not in the dropdown) so the screen can lay
  // a dimming scrim over the whole sheet while the option list floats above it.
  const [dropdownOpen, setDropdownOpen] = useState(initialDropdownOpen)
  const scrimVisible = !error && showDropdown && dropdownOpen

  // The rows are the same in both layouts; only the chrome around them differs
  // (bordered "Pay with" card vs. a bare scrolling list).
  const methodRows = methods.map((method, i) => (
    <Box key={method.id}>
      {i > 0 ? <MethodDivider /> : null}
      <Pressable
        onPress={method.disabled ? undefined : () => onSelectMethod(method.id)}
        disabled={method.disabled}
        accessibilityRole="button"
        accessibilityLabel={method.title}
        accessibilityState={{ disabled: !!method.disabled }}
        style={
          method.disabled
            ? { opacity: theme.opacity.disabled / 100 }
            : undefined
        }
      >
        <PaymentMethodRow method={method} />
      </Pressable>
    </Box>
  ))

  // Sits with the rows, inside the same scroll content — after the list it would
  // be laid out past the sheet's measured height and render outside it.
  const noteText = note ? (
    <Box style={noteBox}>
      <Text
        variant="detail"
        style={{ color: theme.content.elements.secondary }}
      >
        {note}
      </Text>
    </Box>
  ) : null

  return (
    <Box
      borderTopLeftRadius="modal"
      borderTopRightRadius="modal"
      style={{ backgroundColor: theme.background.modal }}
    >
      <Box alignItems="center" style={topBar}>
        <Box
          style={[
            grabber,
            { backgroundColor: theme.border.elements.default.color },
          ]}
        />
        <Text
          variant="bodyMedium"
          style={{ color: theme.content.elements.primary }}
        >
          {title}
        </Text>
      </Box>

      {error ? (
        <SelectMethodError
          title={errorTitle}
          message={errorMessage}
          onRetry={onRetry}
        />
      ) : loading ? (
        <SelectMethodLoading
          count={skeletonCount}
          showDropdown={Boolean(showDropdown)}
        />
      ) : (
        <>
          {/* Fixed "Deposit to" selector. Sits above the scrollable list and
              stays above the scrim (so it reads bright while the rest dims). */}
          {showDropdown ? (
            <Box style={depositSection}>
              <Box style={sectionLabel}>
                <Text
                  variant="detail"
                  weight="medium"
                  style={{ color: theme.content.elements.secondary }}
                >
                  {t('selectMethod.depositTo')}
                </Text>
              </Box>
              <ReceiveTokenDropdown
                tokens={receiveTokens}
                selected={selected}
                open={dropdownOpen}
                onToggle={() => setDropdownOpen((o) => !o)}
                onSelect={(id) => {
                  onSelectReceiveToken?.(id)
                  setDropdownOpen(false)
                }}
              />
            </Box>
          ) : null}

          {showDropdown ? (
            // "Pay with" section: a labeled, bordered card wrapping the method
            // rows with hairline dividers between them (Figma node 11202:8282).
            <Box style={payWithSection}>
              <Box style={sectionLabel}>
                <Text
                  variant="detail"
                  weight="medium"
                  style={{ color: theme.content.elements.secondary }}
                >
                  {t('selectMethod.payWith')}
                </Text>
              </Box>
              <Box
                borderWidth={1}
                borderRadius="element"
                style={[
                  methodsCard,
                  {
                    backgroundColor: theme.background.modal,
                    borderColor: theme.border.elements.default.color,
                  },
                ]}
              >
                {methodRows}
              </Box>
              {noteText}
            </Box>
          ) : (
            // Methods-only: bare rows with full-width hairline dividers, no
            // outer border (Figma node 11202:8632).
            <ScrollView
              contentContainerStyle={listContent}
              showsVerticalScrollIndicator={false}
            >
              {methodRows}
              {noteText}
            </ScrollView>
          )}
        </>
      )}

      {/* Dimming scrim (Figma: background-overlay, rgba(0,0,0,0.2)). Sits below
          the deposit selector + option list but above the rest of the sheet;
          tapping it closes the dropdown. */}
      {scrimVisible ? (
        <AnimatedPressable
          entering={scrimEnter}
          exiting={scrimExit}
          style={scrim}
          onPress={() => setDropdownOpen(false)}
          accessibilityRole="button"
          accessibilityLabel={t('common.close')}
        />
      ) : null}
    </Box>
  )
}

function SelectMethodError({
  title,
  message,
  onRetry,
}: {
  title: string
  message: string
  onRetry?: () => void
}) {
  const { t } = useFunkitTranslation()
  const theme = useTheme()
  const retryLabel = t('common.tryAgain')
  return (
    <Box alignItems="center" justifyContent="center" style={errorContainer}>
      <StatusIcon />
      <Box alignItems="center" style={errorText}>
        <Text
          variant="bodyMedium"
          style={{ color: theme.content.elements.primary }}
        >
          {title}
        </Text>
        <Text
          variant="detail"
          style={[
            errorMessageText,
            { color: theme.content.elements.secondary },
          ]}
        >
          {message}
        </Text>
      </Box>
      {/* No handler → non-retryable state (e.g. withdrawals unavailable in
          region): show the message alone instead of a dead button. */}
      {onRetry ? (
        <Pressable
          onPress={onRetry}
          accessibilityRole="button"
          accessibilityLabel={retryLabel}
        >
          <Box
            borderRadius="button"
            alignItems="center"
            justifyContent="center"
            style={[
              retryButton,
              { backgroundColor: theme.background.button.primary.default },
            ]}
          >
            <Text
              variant="bodyMedium"
              style={{ color: theme.content.button.primary.default }}
            >
              {retryLabel}
            </Text>
          </Box>
        </Pressable>
      ) : null}
    </Box>
  )
}

function PaymentMethodRow({ method }: { method: PaymentMethodOption }) {
  const theme = useTheme()
  return (
    <Box
      flexDirection="row"
      alignItems="center"
      gap="elementsHorizontal"
      style={row}
    >
      <Box width={48} height={48} alignItems="center" justifyContent="center">
        {method.icon}
      </Box>
      <Box flex={1} style={details}>
        <Text
          variant="bodyMedium"
          style={{ color: theme.content.elements.primary }}
        >
          {method.title}
        </Text>
        {method.subtitle ? (
          <Text
            variant="detail"
            style={{ color: theme.content.elements.secondary }}
          >
            {method.subtitle}
          </Text>
        ) : null}
      </Box>
      {method.trailing}
    </Box>
  )
}

/**
 * Hairline divider between method rows (Figma `line-container`, 1px
 * `border/elements/default`), spanning the full row width.
 */
function MethodDivider() {
  const theme = useTheme()
  return (
    <Box
      style={[
        divider,
        { backgroundColor: theme.border.elements.default.color },
      ]}
    />
  )
}

/**
 * The "Deposit to" receive-token dropdown (Figma: Fun Mobile / Add money) —
 * RN analogue of web's `ReceiveTokenDropdown`. A bordered trigger pill
 * (icon + label + chevron); when `open`, the option list floats below it as an
 * absolute overlay (`top: '100%'`) so it never reflows the list underneath.
 * Open-state is controlled by the screen (which also lays the scrim).
 */
function ReceiveTokenDropdown({
  tokens,
  selected,
  open,
  onToggle,
  onSelect,
}: {
  tokens: ReceiveTokenOption[]
  selected: ReceiveTokenOption
  open: boolean
  onToggle: () => void
  onSelect: (id: string) => void
}) {
  const theme = useTheme()
  const selectable = tokens.length > 1

  return (
    <Box style={dropdownAnchor}>
      <Pressable
        onPress={selectable ? onToggle : undefined}
        accessibilityRole="button"
        accessibilityLabel={selected.label}
      >
        <Box
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          borderWidth={1}
          borderRadius="element"
          style={[
            dropdownTrigger,
            {
              backgroundColor: theme.background.modal,
              borderColor: theme.border.elements.default.color,
            },
          ]}
        >
          <Box flexDirection="row" alignItems="center" style={triggerLeft}>
            {selected.icon ?? <ReceiveTokenIconFallback />}
            <Text
              variant="bodyMedium"
              numberOfLines={1}
              style={[triggerLabel, { color: theme.content.elements.primary }]}
            >
              {selected.label}
            </Text>
          </Box>
          {selectable ? <AnimatedChevron open={open} /> : null}
        </Box>
      </Pressable>
      {open && selectable ? (
        <Reanimated.View
          entering={menuEnter}
          exiting={menuExit}
          style={dropdownMenuOverlay}
        >
          <Box
            borderRadius="element"
            style={[dropdownMenu, { backgroundColor: theme.background.modal }]}
          >
            {tokens.map((token) => (
              <Pressable
                key={token.id}
                onPress={() => onSelect(token.id)}
                accessibilityRole="menuitem"
                accessibilityLabel={token.label}
              >
                <Box
                  flexDirection="row"
                  alignItems="center"
                  justifyContent="space-between"
                  style={dropdownItem}
                >
                  <Box
                    flexDirection="row"
                    alignItems="center"
                    style={triggerLeft}
                  >
                    {token.icon ?? <ReceiveTokenIconFallback />}
                    <Text
                      variant="bodyMedium"
                      style={{ color: theme.content.elements.primary }}
                    >
                      {token.label}
                    </Text>
                  </Box>
                  {token.id === selected.id ? <CheckIcon /> : null}
                </Box>
              </Pressable>
            ))}
          </Box>
        </Reanimated.View>
      ) : null}
    </Box>
  )
}

function ReceiveTokenIconFallback() {
  const theme = useTheme()
  return (
    <Box
      width={24}
      height={24}
      borderRadius="rounded"
      style={{ backgroundColor: theme.background.pill.pressed }}
    />
  )
}

function ChevronDownIcon() {
  return <FigmaChevronDownIcon size={16} />
}

/**
 * The trigger chevron, rotated ~180° while the dropdown is open (same
 * timing/easing as the menu) so it reads as flipping up as the menu opens.
 */
function AnimatedChevron({ open }: { open: boolean }) {
  const rotation = useSharedValue(open ? 1 : 0)
  useEffect(() => {
    rotation.value = withTiming(open ? 1 : 0, {
      duration: open ? DROPDOWN_OPEN_DURATION : DROPDOWN_CLOSE_DURATION,
      easing: DROPDOWN_EASING,
    })
  }, [open, rotation])
  // Spelled out because the plugin workletizes by the hook's name, which esbuild
  // renames once a second module imports it — this worked on import order alone.
  const style = useAnimatedStyle(() => {
    'worklet'
    return { transform: [{ rotate: `${rotation.value * 180}deg` }] }
  })
  return (
    <Reanimated.View style={style}>
      <FigmaChevronDownIcon size={16} />
    </Reanimated.View>
  )
}

function CheckIcon() {
  return <FigmaCheckIcon size={16} />
}

/**
 * Loading skeleton for the Add money screen. Mirrors the populated layout so
 * content swaps in without a shift: when `showDropdown`, a "Deposit to"
 * selector skeleton + "Pay with" label sit above the method skeletons (matching
 * the loaded view's sections); otherwise just the method skeletons. Shapes
 * pulse opacity in sync; labels + chevron stay static.
 */
function SelectMethodLoading({
  count,
  showDropdown,
}: {
  count?: number
  showDropdown: boolean
}) {
  const { t } = useFunkitTranslation()
  const theme = useTheme()
  const opacity = usePulse()
  // The deposit selector takes vertical room, so fewer method rows fit.
  const rowCount = count ?? (showDropdown ? 5 : 6)
  return (
    <>
      {showDropdown ? (
        <Box style={depositSection}>
          <Box style={sectionLabel}>
            <Text
              variant="detail"
              style={{ color: theme.content.elements.secondary }}
            >
              {t('selectMethod.depositTo')}
            </Text>
          </Box>
          <Box
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
            borderWidth={1}
            borderRadius="element"
            style={[
              dropdownTrigger,
              {
                backgroundColor: theme.background.modal,
                borderColor: theme.border.elements.default.color,
              },
            ]}
          >
            <Animated.View style={[loadingSelectorLeft, { opacity }]}>
              <Box
                width={20}
                height={20}
                borderRadius="rounded"
                style={{ backgroundColor: theme.background.pill.pressed }}
              />
              <Box
                style={[
                  skeletonTitleBar,
                  { backgroundColor: theme.background.pill.pressed },
                ]}
              />
            </Animated.View>
            <ChevronDownIcon />
          </Box>
        </Box>
      ) : null}

      {showDropdown ? (
        <Box style={payWithSection}>
          <Box style={sectionLabel}>
            <Text
              variant="detail"
              style={{ color: theme.content.elements.secondary }}
            >
              {t('selectMethod.payWith')}
            </Text>
          </Box>
          <Box
            borderWidth={1}
            borderRadius="element"
            style={[
              methodsCard,
              {
                backgroundColor: theme.background.modal,
                borderColor: theme.border.elements.default.color,
              },
            ]}
          >
            <Animated.View
              style={{ opacity }}
              accessibilityLabel={t('selectMethod.loadingMethods')}
            >
              {Array.from({ length: rowCount }, (_, i) => (
                <PaymentMethodRowSkeleton key={i} />
              ))}
            </Animated.View>
          </Box>
        </Box>
      ) : (
        <Box style={skeletonListContent}>
          <Animated.View
            style={{ opacity }}
            accessibilityLabel={t('selectMethod.loadingMethods')}
          >
            {Array.from({ length: rowCount }, (_, i) => (
              <PaymentMethodRowSkeleton key={i} />
            ))}
          </Animated.View>
        </Box>
      )}
    </>
  )
}

/**
 * One skeleton row in the loading list (Figma node 11333:2346): a fixed 68px
 * row (48px circular leading slot + 10px vertical padding) plus two stacked
 * bars (157×16, 83×14, 4px gap) — the exact Figma loading geometry.
 */
function PaymentMethodRowSkeleton() {
  const theme = useTheme()
  return (
    <Box
      flexDirection="row"
      alignItems="center"
      gap="elementsHorizontal"
      style={skeletonRow}
    >
      <Box
        width={48}
        height={48}
        borderRadius="rounded"
        style={{ backgroundColor: theme.background.pill.pressed }}
      />
      <Box flex={1} style={skeletonDetails}>
        <Box
          style={[
            skeletonTitleBar,
            { backgroundColor: theme.background.pill.pressed },
          ]}
        />
        <Box
          style={[
            skeletonSubtitleBar,
            { backgroundColor: theme.background.pill.pressed },
          ]}
        />
      </Box>
    </Box>
  )
}

const topBar: ViewStyle = { height: 64, paddingTop: 25, paddingBottom: 12 }
const grabber: ViewStyle = {
  position: 'absolute',
  top: 8,
  width: 36,
  height: 5,
  borderRadius: 999,
}
// Methods-only list (Figma node 11202:8632): rows padded 16 horizontal, no
// outer border, hairline dividers between them.
// Gap between the last row and the screen bottom (Figma / design review).
const BOTTOM_GAP = 50
const listContent: ViewStyle = {
  paddingHorizontal: 16,
  paddingBottom: BOTTOM_GAP,
}
// list-item/base: 12px padding all sides (Figma), gap 12 between leading slot
// and details supplied by the Box `gap` prop.
const row: ViewStyle = { padding: 12 }
const details: ViewStyle = { gap: 2 }
// The fixed deposit selector. zIndex keeps it (and its overlaid option list)
// above the scrim; the rest of the sheet sits below it and dims.
const depositSection: ViewStyle = {
  paddingHorizontal: 16,
  zIndex: 2,
}
// "Pay with" section: label + bordered method card, 12px gap between (Figma
// node 11202:8279, pt-12 on the section).
const payWithSection: ViewStyle = {
  paddingHorizontal: 16,
  paddingTop: 12,
  paddingBottom: BOTTOM_GAP,
}
// Label rows sit with 12px horizontal padding so they align with the row text
// inside the cards/triggers below them (Figma px-12 on the label).
const sectionLabel: ViewStyle = { paddingHorizontal: 12, paddingBottom: 6 }
// Bordered card wrapping the method rows (Figma node 11202:8282): py-2 inset so
// the first/last rows clear the rounded corners.
const methodsCard: ViewStyle = { paddingVertical: 2, overflow: 'hidden' }

// The enclosing section already pads 16 horizontally; the extra 12 aligns the
// note with the row text above it, as the section labels do.
const noteBox: ViewStyle = { paddingHorizontal: 12, paddingTop: 12 }
// Hairline divider between rows.
const divider: ViewStyle = { height: 1 }
const dropdownAnchor: ViewStyle = { position: 'relative' }
const dropdownTrigger: ViewStyle = { height: 48, paddingHorizontal: 12 }
const triggerLeft: ViewStyle = { gap: 6, flex: 1 }
const triggerLabel: ViewStyle = { flex: 1 }
// Floating option list: anchored just below the trigger as an overlay, so it
// never reflows the method list. The animated wrapper owns the positioning
// (and the open/close transform + opacity); the inner Box owns the surface.
const dropdownMenuOverlay: ViewStyle = {
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  marginTop: 4,
  zIndex: 30,
}
// shadow/lg from Figma (0,8 / blur 32→16 radius / rgba(0,0,0,0.12)); no border.
// Kept on the surface Box (which has a background) so the iOS shadow renders.
const dropdownMenu: ViewStyle = {
  shadowColor: '#000',
  shadowOpacity: 0.12,
  shadowOffset: { width: 0, height: 8 },
  shadowRadius: 16,
  elevation: 8,
}
const dropdownItem: ViewStyle = { height: 48, paddingHorizontal: 12 }
const scrim: ViewStyle = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.2)',
  zIndex: 1,
}
const errorContainer: ViewStyle = {
  height: 468,
  gap: 24,
  paddingHorizontal: 16,
}
const errorText: ViewStyle = { gap: 8 }
const errorMessageText = { textAlign: 'center' } as const
const retryButton: ViewStyle = { height: 48, paddingHorizontal: 20 }
// The loading list (Figma node 11333:2342): same horizontal/bottom padding as
// the loaded list (so the sheet doesn't jump height when loading finishes) but
// no inter-row gap — the 68px skeleton rows stack flush (their py-10 supplies
// the breathing room), matching the Figma frame exactly.
const skeletonListContent: ViewStyle = {
  paddingHorizontal: 16,
  paddingBottom: BOTTOM_GAP,
}
// Figma list-item/base: 12px padding all sides (48px leading circle + 12 each
// side = 72px row). No inter-row gap; rows abut directly inside the card.
const skeletonRow: ViewStyle = { padding: 12 }
// Row layout for the skeleton selector's left (Animated.View takes no Restyle
// props, so flexDirection lives here, not as a prop).
const loadingSelectorLeft: ViewStyle = {
  flexDirection: 'row',
  alignItems: 'center',
  gap: 6,
  flex: 1,
}
const skeletonDetails: ViewStyle = { gap: 4 }
const skeletonTitleBar: ViewStyle = { width: 157, height: 16, borderRadius: 4 }
const skeletonSubtitleBar: ViewStyle = {
  width: 83,
  height: 14,
  borderRadius: 4,
}
